import Link from 'next/link'
import { CSS, keyframes } from 'style'

import { websitePaths } from 'domains/website/website.paths'

import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import { NavbarDropdown } from 'components/pages/@layouts/layout-website/navbar-dropdown'

import { NavbarLinkProps } from './links'

const unfold = keyframes({
  '0%': { transform: 'rotateX(-40deg)' },
  '50%': { transform: 'rotateX(-10deg)' },
  '100%': { transform: 'rotateX(0deg)' },
})

const animation: CSS = {
  transformOrigin: 'var(--radix-popover-content-transform-origin)',
  animation: `${unfold} 0.25s ease-in forwards`,
}

const DropdownItem = (props: Omit<NavbarLinkProps, 'href'>) => {
  const { name, description, icon } = props

  return (
    <Box
      flex
      css={{
        size: '$full',
        spaceX: 2,
        p: '$3',
        mx: '-$3',
        borderRadius: '$lg',
        border: '1px solid transparent',
        transition: '.1s ease-in',
        '&:hover': { bgc: '$bg1', borderColor: '$bg3' },
      }}
    >
      {!!icon && (
        <Box>
          <Box flex justify="center" items="center" css={{ size: 38 }}>
            <IconStyled as={icon} size="2xl" color="text1" />
          </Box>
        </Box>
      )}
      <Box flex="column">
        <Text weight="semiBold">{name}</Text>
        {description && (
          <Text size="sm" color="text2">
            {description}
          </Text>
        )}
      </Box>
    </Box>
  )
}

type NavbarDropdownProps = {
  name: string
  items: NavbarLinkProps[]
}

export const NavbarDropdownSolutions = (props: NavbarDropdownProps) => {
  const { name, items } = props

  const useCases = [
    {
      href: websitePaths.commerce,
      name: 'Commerce',
    },
    {
      href: websitePaths.mobile,
      name: 'Mobile Apps',
    },
    {
      href: websitePaths.web3,
      name: 'Blockchain and web3',
    },
    {
      href: websitePaths.bff,
      name: 'Backend for Frontend',
    },
    {
      href: websitePaths.ai,
      name: 'AI',
    },
    {
      href: websitePaths.logistics,
      name: 'Logistics',
    },
  ]

  return (
    <NavbarDropdown name={name}>
      <Box
        css={{
          mt: '$6',
          '@md': {
            minWidth: '70vw',
          },
          '@lg': {
            minWidth: '50vw',
          },
          '&:focus': { outline: 'none' },
          ...animation,
        }}
      >
        <Box
          css={{
            container: 'sm',
            px: '$5',
            bgc: '$bg2',
            border: '1px solid $bg3',
            borderRadius: '$md',
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2)',
            '.dark &': {
              boxShadow: 'none',
            },
            '@md': {
              container: 'md',
              px: '$10',
            },
            '@lg': {
              container: 'full',
              pr: '$0',
            },
          }}
        >
          <Box flex css={{ gridCols: 2 }}>
            <Box
              css={{
                borderRight: '1px solid $bg3',
                py: '$4',
                flex: 1,
              }}
            >
              <Text as="p" color="text2" size="sm">
                Solutions
              </Text>
              <Box flex="column" css={{ mt: '$4', gap: '$2' }}>
                {items.map(({ href, ...rest }) => {
                  const isExternalLink = href.startsWith('https://')
                  if (isExternalLink) {
                    return (
                      <Text
                        as="a"
                        key={href}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DropdownItem {...rest} />
                      </Text>
                    )
                  }
                  return (
                    <Link key={href} href={href}>
                      <DropdownItem {...rest} />
                    </Link>
                  )
                })}
              </Box>
            </Box>
            <Box css={{ mt: '$4', flex: 1 }}>
              <Box css={{ borderBottom: '1px solid $bg3', pb: '$4', pl: '$6' }}>
                <Text as="p" color="text2" size="sm">
                  Use Cases
                </Text>
                <Box grid css={{ mt: '$4' }}>
                  {useCases.map(({ href, ...rest }) => (
                    <Link key={href} href={href}>
                      <DropdownItem {...rest} />
                    </Link>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </NavbarDropdown>
  )
}
